import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ScanLine, CheckCircle2, AlertTriangle, ArrowRight, Stethoscope, Clock, User
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import * as bookingsApi from '../../api/bookings.api';
import { errorMessage } from '../../api/client';
import { formatDateTime } from '../../utils/format';

// Clinic counter check-in. The customer presents their booking pass; staff
// either scans the QR (paste the qrToken) or types in the AIH-XXXX. Server
// accepts both. On success the booking flips BOOKED → ARRIVED.

function CheckIn() {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null); // last successful check-in (for confirmation card)
  const [err, setErr] = useState('');

  async function submit(e) {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setBusy(true); setErr('');
    try {
      const order = await bookingsApi.checkInBooking(code.trim());
      setLast(order);
      setCode('');
      toast.success('Checked in');
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Customer check-in"
        subtitle="Scan the customer's QR or type the AIH-XXXX booking ID to mark them as arrived."
      />

      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-title">
          <h3><ScanLine size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Check in</h3>
        </div>

        <form onSubmit={submit}>
          <div className="field">
            <label>Booking ID or QR token</label>
            <input
              className="input"
              autoFocus
              placeholder="AIH-XXXX or paste scanned QR token"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 18, letterSpacing: 1,
              }}
            />
            <div className="hint">Tip: a USB QR scanner types straight into this field.</div>
          </div>

          {err && (
            <div className="notice danger" style={{ marginTop: 12 }}>
              <AlertTriangle size={16} style={{ verticalAlign: '-3px', marginRight: 6 }} />
              {err}
            </div>
          )}

          <div className="row" style={{ marginTop: 'var(--s-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" type="submit" disabled={busy || !code.trim()}>
              {busy ? 'Checking in…' : <>Check in <ArrowRight size={14} /></>}
            </button>
          </div>
        </form>
      </div>

      {/* Last successful check-in card */}
      {last && (
        <div className="card" style={{ marginTop: 'var(--s-4)', borderColor: 'var(--brand-200)', background: 'var(--brand-50)' }}>
          <div className="card-title">
            <h3><CheckCircle2 size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Checked in</h3>
            <Link className="text-sm" to={`/orders/${last._id}`}>Open booking <ArrowRight size={14} /></Link>
          </div>

          <dl className="kvs">
            <dt><User size={12} /> Customer</dt>
            <dd>{last.user?.name || last.user?.email || '—'}</dd>
            <dt>Booking ID</dt>
            <dd className="cell-mono" style={{ fontWeight: 700 }}>{last.bookingId}</dd>
            <dt>Plan</dt>
            <dd>{last.package?.name || last.packageSnapshot?.name || '—'}</dd>
            {last.scheduledAt && (
              <>
                <dt><Clock size={12} /> Scheduled</dt>
                <dd>{formatDateTime(last.scheduledAt)}</dd>
              </>
            )}
            <dt><Stethoscope size={12} /> Status</dt>
            <dd>{last.status}</dd>
          </dl>

          <div className="muted text-sm" style={{ marginTop: 12 }}>
            Customer is now ARRIVED. Open their booking to start the consultation and update treatment status.
          </div>
        </div>
      )}
    </>
  );
}

export default CheckIn;
